import { MAX_APP_FONT_SIZE, MIN_APP_FONT_SIZE } from "@cmmd-forge/contracts/settings";
import type { SidebarLayoutMode } from "@cmmd-forge/contracts/settings";
import type { Theme } from "../../hooks/useTheme";
import type { ReactElement } from "react";

import { cn } from "../../lib/utils";
import { Input } from "../ui/input";

const THEME_OPTIONS: ReadonlyArray<{ readonly value: Theme; readonly label: string }> = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
];

/** Small circular hint of what each theme controls. */
function ThemeSwatch({ value }: { readonly value: Theme }): ReactElement {
  if (value === "system") {
    return (
      <span className="grid size-3.5 shrink-0 grid-cols-2 overflow-hidden rounded-full border border-border">
        <span className="bg-background" />
        <span className="bg-foreground" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "size-3.5 shrink-0 rounded-full border border-border",
        value === "light" ? "bg-background" : "bg-foreground",
      )}
    />
  );
}

/**
 * Both layouts described in the terms a new user can act on: what the list is
 * made of, and what the project picker does. Naming the trade-off here means
 * the choice is informed the first time it is offered, not discovered later in
 * Settings.
 */
const SIDEBAR_LAYOUT_OPTIONS: ReadonlyArray<{
  readonly value: SidebarLayoutMode;
  readonly label: string;
  readonly detail: string;
}> = [
  {
    value: "activity",
    label: "Activity",
    detail: "One list of threads, newest first. Projects filter it.",
  },
  {
    value: "projects",
    label: "Projects",
    detail: "One row per project, with its threads nested underneath.",
  },
];

function SetupFontSizeRow(props: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (fontSize: number) => void;
}): ReactElement {
  return (
    <label className="flex min-h-12 items-center justify-between gap-4 border-b border-border px-3 py-2 last:border-b-0">
      <span className="text-sm font-medium text-foreground">{props.label}</span>
      <span className="flex items-center gap-2">
        <Input
          nativeInput
          type="number"
          min={MIN_APP_FONT_SIZE}
          max={MAX_APP_FONT_SIZE}
          step={1}
          value={props.value}
          aria-label={props.label}
          onChange={(event) => {
            const nextValue = Number.parseInt(event.currentTarget.value, 10);
            if (Number.isFinite(nextValue)) {
              props.onChange(nextValue);
            }
          }}
          className="h-8 w-16 text-right tabular-nums"
        />
        <span className="text-xs text-muted-foreground">px</span>
      </span>
    </label>
  );
}

export function FirstAccountSetupAppearancePanel(props: {
  readonly theme: Theme;
  readonly uiFontSize: number;
  readonly codeFontSize: number;
  readonly sidebarLayoutMode: SidebarLayoutMode;
  readonly onThemeChange: (theme: Theme) => void;
  readonly onUiFontSizeChange: (fontSize: number) => void;
  readonly onCodeFontSizeChange: (fontSize: number) => void;
  readonly onSidebarLayoutModeChange: (mode: SidebarLayoutMode) => void;
}): ReactElement {
  return (
    <div className="mt-6">
      {/* Compact segmented selector: three equal segments read at a glance and
          stay one tap away at every width (the old preview cards stacked into
          three tall blocks on mobile). */}
      <div
        role="radiogroup"
        aria-label="Theme"
        className="grid grid-cols-3 gap-1 rounded-lg border border-border bg-muted p-1"
      >
        {THEME_OPTIONS.map((option) => {
          const selected = props.theme === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-pressed={selected}
              onClick={() => props.onThemeChange(option.value)}
              className={cn(
                "flex min-h-9 items-center justify-center gap-2 rounded-md px-2 text-sm font-medium transition-colors",
                selected
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <ThemeSwatch value={option.value} />
              {option.label}
            </button>
          );
        })}
      </div>
      {/* Two options with real consequences, so each carries its one-line
          explanation rather than relying on the label alone. */}
      <div
        role="radiogroup"
        aria-label="Sidebar layout"
        className="mt-4 grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted p-1"
      >
        {SIDEBAR_LAYOUT_OPTIONS.map((option) => {
          const selected = props.sidebarLayoutMode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-pressed={selected}
              onClick={() => props.onSidebarLayoutModeChange(option.value)}
              className={cn(
                "flex min-h-9 flex-col items-start justify-center gap-0.5 rounded-md px-2.5 py-2 text-left transition-colors",
                selected
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="text-sm font-medium">{option.label}</span>
              <span className="text-xs text-muted-foreground">{option.detail}</span>
            </button>
          );
        })}
      </div>
      <section className="mt-4 overflow-hidden rounded-lg border border-border bg-background/55">
        <SetupFontSizeRow
          label="UI font size"
          value={props.uiFontSize}
          onChange={props.onUiFontSizeChange}
        />
        <SetupFontSizeRow
          label="Code font size"
          value={props.codeFontSize}
          onChange={props.onCodeFontSizeChange}
        />
      </section>
    </div>
  );
}
